import { ParsedUrlQuery } from "querystring";
import { IPaginationItem, IPaginationProp } from "./interfaces";

const newPageItem = (
  page: number,
  isActive: boolean,
  isDisabled: boolean,
  label: string | number,
  href: string
): IPaginationItem => {
  return {
    page,
    isActive,
    isDisabled,
    label,
    href,
  };
};

export const PaginationModel = class {
  private _pathName: string;
  private _query: ParsedUrlQuery;
  private _currentPage: number;
  private _limit: number;
  private _totalPages: number;
  private _totalRecords: number;
  private _pageItemLength: number;
  private _type: "default" | "link" | "callback";
  private _callback: (page: number | undefined) => void;

  private _useFirstAction: boolean;
  private _usePrevAction: boolean;
  private _useNextAction: boolean;
  private _useLastAction: boolean;

  private _pages: Array<IPaginationItem>;

  // private _firstPage: number;
  // private _lastPage: number;
  // private _firstPaginationRangeNumber: number;
  // private _lastPaginationRangeNumber: number;

  constructor() {
    this._query = {};
    this._pathName = "";
    this._pages = [];
    this._currentPage = 1;
    this._limit = 10;
    this._totalPages = 1;
    this._totalRecords = 0;
    this._pageItemLength = 10;
    this._type = "default";
    this._callback = (page: number | undefined) => {};
    this._useFirstAction = true;
    this._usePrevAction = true;
    this._useNextAction = true;
    this._useLastAction = true;
  }

  set setPathName(pathname: string) {
    this._pathName = pathname;
  }

  set setParsedQuery(query: ParsedUrlQuery) {
    this._query = query;
  }

  set setCurrentPage(currentPage: number | string | undefined | null) {
    if (currentPage) {
      this._currentPage = +currentPage || 1;
    }
  }

  set setLimit(limit: number | undefined | null) {
    if (limit) {
      this._limit = limit;
    }
  }

  set setTotalPages(totalPages: number | undefined | null) {
    if (totalPages) {
      this._totalPages = totalPages;
    }
  }

  set setTotalRecords(totalRecords: number | undefined | null) {
    if (totalRecords) {
      this._totalRecords = totalRecords;
    }
  }

  set setPageItemLength(pageItemLength: number | undefined | null) {
    if (pageItemLength) {
      this._pageItemLength = pageItemLength;
    }
  }

  set setType(type: "default" | "link" | "callback") {
    this._type = type;
  }

  set setCallback(callback: (page: number | undefined) => void) {
    if (callback) {
      this._callback = callback;
    }
  }

  set setUseFirstAction(useFirstAction: boolean) {
    this._useFirstAction = useFirstAction;
  }

  set setUsePrevAction(usePrevAction: boolean) {
    this._usePrevAction = usePrevAction;
  }

  set setUseNextAction(useNextAction: boolean) {
    this._useNextAction = useNextAction;
  }

  set setUseLastAction(useLastAction: boolean) {
    this._useLastAction = useLastAction;
  }

  makeHref(pageNumber: number) {
    const queries = [];
    let writePageProperty = false;
    for (let key in this._query) {
      queries.push(key === "page" ? `${key}=${pageNumber}` : `${key}=${this._query[key]}`);
      if (key === "page") {
        writePageProperty = true;
      }
    }

    if (writePageProperty === false) {
      queries.push(`page=${pageNumber}`);
    }

    return `${this._pathName}?${queries.join("&")}`;
  }

  private _getFirstPage() {
    const firstPage = 1;
    return newPageItem(
      firstPage,
      firstPage !== this._currentPage,
      !this._useFirstAction,
      firstPage,
      this.makeHref(firstPage)
    );
  }

  private _getPrevPage() {
    const prevPage = this._currentPage - 1 > 0 ? this._currentPage - 1 : 1;
    return newPageItem(
      prevPage,
      prevPage !== this._currentPage,
      !this._usePrevAction,
      prevPage,
      this.makeHref(prevPage)
    );
  }

  private _getNextPage() {
    const nextPage = this._currentPage + 1 <= this._totalPages ? this._currentPage + 1 : this._totalPages;
    return newPageItem(
      nextPage,
      nextPage !== this._currentPage,
      !this._useNextAction,
      nextPage,
      this.makeHref(nextPage)
    );
  }

  private _getLastPage() {
    const lastPage = this._totalPages;
    return newPageItem(
      lastPage,
      lastPage !== this._currentPage,
      !this._useLastAction,
      lastPage,
      this.makeHref(lastPage)
    );
  }

  getItems(): IPaginationProp {
    const firstPage = (this._currentPage - 1) * this._limit + 1;
    const lastPage = this._totalPages;
    const firstPaginationRangeNumber =
      Math.floor((this._currentPage - 1) / this._pageItemLength) * this._pageItemLength + 1;
    const lastPaginationRangeNumber = Math.min(firstPaginationRangeNumber + this._pageItemLength - 1, lastPage);
    // const paginationRange: number[] = Array.from({ length: lastPaginationRangeNumber - firstPaginationRangeNumber + 1 }, (v, k) => k + firstPaginationRangeNumber)
    const paginationRange: Array<number> = Array.from(
      { length: lastPaginationRangeNumber - firstPaginationRangeNumber + 1 },
      (v, k) => k + firstPaginationRangeNumber
    );

    const pageItems: IPaginationItem[] = [];
    for (const page of paginationRange) {
      const newItem = newPageItem(page, page === this._currentPage, false, page, this.makeHref(page));
      pageItems.push(newItem);
    }

    return {
      type: this._type,
      callback: this._callback,
      first: this._getFirstPage(),
      prev: this._getPrevPage(),
      pages: pageItems,
      next: this._getNextPage(),
      last: this._getLastPage(),
      info: {
        currentPage: this._currentPage,
        totalPages: this._totalPages,
        totalRecords: this._totalRecords,
      },
    };
  }
};
